/**
 * Whether onboarding must hold back the launch because the session's invoice is not paid.
 *
 * Deliberately free of store / window imports so the rule stays a pure, unit-testable function
 * (same reasoning as [`./session-invoice`](./session-invoice.ts)). The Redux selector
 * `selectDeployBlockedUnpaid` is a thin wrapper over it.
 */

/**
 * True only when we POSITIVELY know the invoice has not been paid.
 *
 * This exists because of the asynchronous SPEI rail. A card charge clears before onboarding
 * begins, so `payment_successful` implies the money arrived. A bank transfer does not: the
 * backend can answer `can_continue: true` and hand the owner straight into onboarding while the
 * transfer is still in flight, so they can reach the deploy step with nothing paid.
 *
 * **Fails OPEN, and the asymmetry is the whole point.** Letting an unpaid brand go live costs
 * a free casino; wrongly blocking a paid one strands a customer who has already handed over
 * money and can see no way forward. So an unknown status — no invoice row, a failed read, a
 * rail that never reports one — must never block.
 */
export function isDeployBlockedUnpaid(invoiceStatus: string | null | undefined): boolean {
  // An EMPTY string is "unknown", not "unpaid" — same as null. Treating it as a real status
  // would block on a backend that returns `""` for a status it hasn't computed yet.
  const status = typeof invoiceStatus === 'string' ? invoiceStatus.trim() : '';
  return status !== '' && status !== 'paid';
}

/** What the deploy step knows when it decides whether to read the invoice status. */
export interface InvoiceReadPrecondition {
  /** This mount has already issued its one read. */
  alreadyAsked: boolean;
  /** `/session/load` has RESOLVED, so the envelope's `invoice_id` is in the store. */
  sessionLoaded: boolean;
  /** A status is already known (from the envelope, the websocket, or an earlier read). */
  statusKnown: boolean;
}

/**
 * Whether the deploy step should issue its one invoice-status read yet.
 *
 * **Waiting for `/session/load` is the load-bearing part.** The answer to this read is only
 * believed when it can be attributed to the session's own invoice (see
 * `isAttributableInvoiceRead`), and the id it is matched against arrives with the session
 * envelope. This step mounts off a `sessionId` rehydrated synchronously from localStorage, so
 * without this gate the read is issued first, wins the race against the envelope, and is then
 * discarded as unattributable — while the once-per-mount latch has already been set, so nothing
 * ever asks again. The status stays unknown, and unknown FAILS OPEN: a brand whose bank transfer
 * never arrived goes live. Asking a beat later costs nothing; asking too early costs the gate.
 */
export function shouldReadInvoiceStatus(s: InvoiceReadPrecondition): boolean {
  return !s.alreadyAsked && s.sessionLoaded && !s.statusKnown;
}
